/**
 * Santé — les personnes du foyer et leur frise d'échéances.
 *
 * Le nombre de personnes est libre. La frise est déduite de la seule date de
 * naissance : ce qui est passé s'archive, ce qui vient est mis en avant, et
 * chaque entrée porte son origine et sa prise en charge.
 *
 * Le sexe reste facultatif, avec « ne pas préciser » comme réponse pleinement
 * valable : il ne sert qu'aux dépistages qui en dépendent, et jamais à autre
 * chose.
 */

import { useState } from 'react';
import { ArrowLeft, Plus, RefreshCw, UserPlus } from 'lucide-react';

import { type Personne, type Tache, aujourdhuiParis, enClair, joursEntre, relatif } from '@foyer/noyau';

import { BadgesTache, Champ, Chargement, Erreur, Feuille, TRAIT, Vide } from '../composants/ui.js';
import { useAppli, useRessource, navigue } from '../lib/appli.js';
import { donnees } from '../lib/donnees.js';
import { FeuilleTache } from './FeuilleTache.js';

function age(naissance: string, aujourdhui: string): string {
  const jours = joursEntre(naissance, aujourdhui);
  const ans = Math.floor(jours / 365.25);
  if (ans >= 2) return `${ans} ans`;
  const mois = Math.floor(jours / 30.44);
  return mois <= 1 ? `${jours} jours` : `${mois} mois`;
}

export function Sante() {
  const aujourdhui = aujourdhuiParis();
  const personnes = useRessource(() => donnees.personnes());
  const taches = useRessource(() => donnees.taches({ statut: 'active', categorie: 'sante' }));
  const [ajout, setAjout] = useState(false);

  if (personnes.chargement && !personnes.donnee) return <Chargement quoi="Chargement du foyer" />;
  if (personnes.erreur && !personnes.donnee) return <Erreur message={personnes.erreur} />;

  const liste = personnes.donnee?.personnes ?? [];
  const toutes = taches.donnee?.taches ?? [];

  return (
    <>
      <div className="entete-vue">
        <h1>
          Santé
        </h1>
        {liste.length > 0 && (
          <button type="button" className="bouton" onClick={() => setAjout(true)}>
            <UserPlus strokeWidth={TRAIT} size={18} aria-hidden />
            Ajouter
          </button>
        )}
      </div>

      {liste.length === 0 ? (
        <Vide
          titre="Personne pour l’instant."
          texte="Ajoutez les membres du foyer : à partir de la date de naissance, l’application déduit le calendrier vaccinal et les examens obligatoires."
        >
          <button type="button" className="bouton engagement" onClick={() => setAjout(true)}>
            Ajouter une personne
          </button>
        </Vide>
      ) : (
        <div className="grille-cartes deux">
          {liste.map((p) => {
            const siennes = toutes
              .filter((t) => t.personneId === p.id && t.echeance)
              .sort((a, b) => a.echeance!.localeCompare(b.echeance!));
            const prochaine = siennes[0];
            return (
              <button
                key={p.id}
                type="button"
                className="carte"
                style={{ textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer', margin: 0 }}
                onClick={() => navigue(`/sante/${p.id}`)}
              >
                <div className="entre-deux">
                  <strong>{p.prenom}</strong>
                  <span className="badge">{age(p.naissance, aujourdhui)}</span>
                </div>
                <div className="sourdine">né(e) le {enClair(p.naissance)}</div>
                <div style={{ marginTop: 8, fontSize: 13.5 }}>
                  {prochaine
                    ? `Prochaine : ${prochaine.titre.replace(`${p.prenom} — `, '')} — ${relatif(prochaine.echeance!, aujourdhui).texte.toLowerCase()}`
                    : 'Aucune échéance à venir.'}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {ajout && <FeuillePersonne onFerme={() => setAjout(false)} />}
    </>
  );
}

export function FrisePersonne({ id }: { id: string }) {
  const { rafraichis } = useAppli();
  const aujourdhui = aujourdhuiParis();
  const fiche = useRessource(() => donnees.personne(id), [id]);
  const catalogues = useRessource(() => donnees.catalogues());
  const [message, setMessage] = useState<string | null>(null);
  const [feuille, setFeuille] = useState<Tache | 'nouvelle' | null>(null);
  const [occupe, setOccupe] = useState(false);

  if (fiche.chargement && !fiche.donnee) return <Chargement quoi="Chargement de la frise" />;
  if (fiche.erreur && !fiche.donnee) return <Erreur message={fiche.erreur} />;
  const personne = fiche.donnee?.personne;
  if (!personne) return <Erreur message="Personne introuvable" />;

  const taches = (fiche.donnee?.taches ?? [])
    .filter((t) => t.echeance)
    .sort((a, b) => a.echeance!.localeCompare(b.echeance!));
  const passees = taches.filter((t) => t.statut !== 'active' || joursEntre(t.echeance!, aujourdhui) > 0);
  const aVenir = taches.filter((t) => !passees.includes(t));

  async function recalcule() {
    setOccupe(true);
    try {
      const r = await donnees.regenereSante(id);
      setMessage(`${r.creees.length} échéance(s) ajoutée(s), ${r.misesAJour.length} mise(s) à jour.`);
      rafraichis();
      fiche.recharge();
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setOccupe(false);
    }
  }

  return (
    <>
      <div className="entete-vue">
        <h1>{personne.prenom}</h1>
        <button type="button" className="bouton texte" onClick={() => navigue('/sante')}>
          <ArrowLeft strokeWidth={TRAIT} size={16} aria-hidden />
          Santé
        </button>
      </div>
      <p className="chapeau">
        {age(personne.naissance, aujourdhui)} · né(e) le {enClair(personne.naissance)}
        {personne.sexe ? '' : ' · sexe non précisé'}
      </p>

      {catalogues.donnee?.sante.avertissement && (
        <div className="note">⚠ {catalogues.donnee.sante.avertissement}</div>
      )}
      {!personne.sexe && (
        <div className="note">
          Le sexe n’est pas renseigné : les dépistages qui en dépendent (mammographie, col de
          l’utérus…) ne sont pas proposés. C’est un champ facultatif.
        </div>
      )}
      {message && <div className="succes">{message}</div>}

      <div className="section-titre" style={{ cursor: 'default' }}>
        À venir <span className="compte">{aVenir.length}</span>
      </div>
      <div className="frise">
        {aVenir.length === 0 && <div className="note">Aucune échéance à venir.</div>}
        {aVenir.map((t, i) => (
          <div key={t.id} className={`jalon${i === 0 ? ' prochain' : ''}`}>
            <button
              type="button"
              onClick={() => setFeuille(t)}
              style={{
                background: 'none',
                border: 0,
                padding: 0,
                font: 'inherit',
                textAlign: 'left',
                color: 'inherit',
                cursor: 'pointer',
              }}
            >
              <strong>{t.titre.replace(`${personne.prenom} — `, '')}</strong>
            </button>
            <div className="sourdine">
              {enClair(t.echeance!)} · {relatif(t.echeance!, aujourdhui).texte.toLowerCase()}
            </div>
            <BadgesTache tache={t} />
          </div>
        ))}
      </div>

      {passees.length > 0 && (
        <>
          <div className="section-titre" style={{ cursor: 'default' }}>
            Archivé <span className="compte">{passees.length}</span>
          </div>
          <div className="frise">
            {passees.map((t) => (
              <div key={t.id} className="jalon passe">
                <div className="sourdine">
                  {enClair(t.echeance!)} — {t.titre.replace(`${personne.prenom} — `, '')}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="actions">
        <button type="button" className="bouton secondaire" onClick={() => setFeuille('nouvelle')}>
          <Plus strokeWidth={TRAIT} size={16} aria-hidden />
          Ajouter un suivi
        </button>
        <button type="button" className="bouton secondaire" onClick={() => void recalcule()} disabled={occupe}>
          <RefreshCw strokeWidth={TRAIT} size={16} aria-hidden />
          Recalculer
        </button>
      </div>
      <p className="sourdine">
        Un suivi ajouté à la main — allergologue, orthodontiste, renouvellement d’ordonnance — n’est
        pas un citoyen de seconde zone : il vit exactement comme les autres.
      </p>

      {feuille && (
        <FeuilleTache
          tache={feuille === 'nouvelle' ? undefined : feuille}
          categorieInitiale="sante"
          personneId={personne.id}
          titreInitial={feuille === 'nouvelle' ? `${personne.prenom} — ` : undefined}
          onFerme={() => setFeuille(null)}
          onEnregistre={() => fiche.recharge()}
        />
      )}
    </>
  );
}

function FeuillePersonne({ onFerme }: { onFerme(): void }) {
  const { rafraichis } = useAppli();
  const [prenom, setPrenom] = useState('');
  const [naissance, setNaissance] = useState('');
  const [sexe, setSexe] = useState('');
  const [type, setType] = useState<'adulte' | 'enfant'>('enfant');
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [bilan, setBilan] = useState<string | null>(null);

  async function enregistre() {
    setOccupe(true);
    setErreur(null);
    try {
      const r = await donnees.creePersonne({
        prenom,
        naissance,
        sexe: sexe || null,
        type,
      });
      rafraichis();
      setBilan(
        `${r.creees.length} échéance(s) programmée(s) pour ${r.personne.prenom}. ${
          r.ignorees.filter((i) => i.motif === 'deja_passee').length
        } jalon(s) déjà passé(s) ont été archivés.`,
      );
    } catch (err) {
      setErreur((err as Error).message);
    } finally {
      setOccupe(false);
    }
  }

  return (
    <Feuille
      titre="Ajouter une personne"
      onFerme={onFerme}
      pied={
        bilan ? (
          <button type="button" className="bouton" onClick={onFerme}>
            Terminé
          </button>
        ) : (
          <button
            type="button"
            className="bouton engagement"
            onClick={() => void enregistre()}
            disabled={occupe || !prenom.trim() || !naissance}
          >
            {occupe ? 'Calcul du calendrier…' : 'Ajouter et déduire le calendrier'}
          </button>
        )
      }
    >
      {bilan ? (
        <div className="succes">{bilan}</div>
      ) : (
        <>
          <Champ etiquette="Prénom">
            <input value={prenom} onChange={(e) => setPrenom(e.target.value)} autoFocus />
          </Champ>
          <Champ etiquette="Date de naissance" aide="C’est elle qui détermine tout le calendrier.">
            <input type="date" value={naissance} onChange={(e) => setNaissance(e.target.value)} />
          </Champ>
          <div className="rangee">
            <Champ etiquette="Adulte ou enfant">
              <select value={type} onChange={(e) => setType(e.target.value as 'adulte' | 'enfant')}>
                <option value="enfant">enfant</option>
                <option value="adulte">adulte</option>
              </select>
            </Champ>
            <Champ
              etiquette="Sexe"
              aide="Facultatif. Sert uniquement aux dépistages qui en dépendent."
            >
              <select value={sexe} onChange={(e) => setSexe(e.target.value)}>
                <option value="">ne pas préciser</option>
                <option value="F">féminin</option>
                <option value="M">masculin</option>
              </select>
            </Champ>
          </div>
          {erreur && <div className="alerte">{erreur}</div>}
        </>
      )}
    </Feuille>
  );
}
